import type { Router, RouteLocationNormalizedLoaded } from 'vue-router';
import {
  captureEmbedOrganizationContext,
  getEmbedNavigationHomeUrl,
  isEmbeddedContext,
  navigateToEmbedOrganizationHome,
  resolveEmbedOrganizationContext,
} from '@/utils/embeddedUtils';

export const CHECKOUT_RETURN_PATH_KEY = 'checkoutReturnPath';
export const CHECKOUT_RETURN_LABEL_KEY = 'checkoutReturnLabel';

const MARKETPLACE_PATH = '/web/dashboard';
const DEFAULT_MARKETPLACE_LABEL = 'Browse Marketplace';
const DEFAULT_HOME_LABEL = 'Back to Home';

export interface CheckoutReturnContext {
  path: string;
  label?: string;
}

function isCheckoutPath(path: string): boolean {
  return path.startsWith('/web/checkout')
    || path.startsWith('/web/ticket/checkout')
    || path.startsWith('/payment/intent/cart');
}

export function isSafeCheckoutReturnPath(path: string): boolean {
  if (!path.startsWith('/')) return false;
  return !isCheckoutPath(path);
}

export function saveCheckoutReturn(context: CheckoutReturnContext): void {
  if (!context.path || !isSafeCheckoutReturnPath(context.path)) return;

  sessionStorage.setItem(CHECKOUT_RETURN_PATH_KEY, context.path);
  if (context.label) {
    sessionStorage.setItem(CHECKOUT_RETURN_LABEL_KEY, context.label);
  } else {
    sessionStorage.removeItem(CHECKOUT_RETURN_LABEL_KEY);
  }
}

export function loadCheckoutReturn(): CheckoutReturnContext | null {
  const path = sessionStorage.getItem(CHECKOUT_RETURN_PATH_KEY);
  if (!path || !isSafeCheckoutReturnPath(path)) return null;

  const label = sessionStorage.getItem(CHECKOUT_RETURN_LABEL_KEY) || undefined;
  return { path, label };
}

export function clearCheckoutReturn(): void {
  sessionStorage.removeItem(CHECKOUT_RETURN_PATH_KEY);
  sessionStorage.removeItem(CHECKOUT_RETURN_LABEL_KEY);
}

export function persistCheckoutReturnFromRoute(
  route: RouteLocationNormalizedLoaded,
  options?: { overwrite?: boolean },
): void {
  if (!options?.overwrite && loadCheckoutReturn()) {
    return;
  }

  const returnTo = typeof route.query.returnTo === 'string' ? route.query.returnTo : null;
  const returnLabel = typeof route.query.returnLabel === 'string'
    ? route.query.returnLabel
    : undefined;

  if (returnTo && isSafeCheckoutReturnPath(returnTo)) {
    saveCheckoutReturn({ path: returnTo, label: returnLabel });
    return;
  }

  captureEmbedOrganizationContext(route);
  const embedHome = getEmbedNavigationHomeUrl(resolveEmbedOrganizationContext(route));
  if (embedHome && (isEmbeddedContext() || route.query.embedOrganizationId)) {
    saveCheckoutReturn({ path: embedHome, label: returnLabel || 'Shop' });
  }
}

export function resolveCheckoutReturnLabel(
  route?: RouteLocationNormalizedLoaded,
  options?: { forSuccess?: boolean },
): string {
  const saved = loadCheckoutReturn();
  if (saved?.label) {
    return `Back to ${saved.label}`;
  }

  if (route) {
    const embedContext = resolveEmbedOrganizationContext(route);
    if (embedContext.organizationId && (isEmbeddedContext() || route.query.embedOrganizationId)) {
      return 'Back to Shop';
    }
  }

  return options?.forSuccess ? DEFAULT_HOME_LABEL : DEFAULT_MARKETPLACE_LABEL;
}

export function navigateCheckoutReturn(
  router: Router,
  route?: RouteLocationNormalizedLoaded,
  options?: { fallbackPath?: string },
): void {
  const fallback = options?.fallbackPath || MARKETPLACE_PATH;
  const saved = loadCheckoutReturn();

  if (saved?.path) {
    clearCheckoutReturn();
    router.push(saved.path);
    return;
  }

  if (route) {
    const returnTo = typeof route.query.returnTo === 'string' ? route.query.returnTo : null;
    if (returnTo && isSafeCheckoutReturnPath(returnTo)) {
      clearCheckoutReturn();
      router.push(returnTo);
      return;
    }
  }

  if (route && isEmbeddedContext()) {
    const embedContext = resolveEmbedOrganizationContext(route);
    const navigated = navigateToEmbedOrganizationHome(route, {
      organizationId: embedContext.organizationId || undefined,
      embedSource: embedContext.embedSource || undefined,
      stickerId: embedContext.stickerId || undefined,
      standalone: embedContext.standalone,
    }, { router });

    if (navigated) {
      clearCheckoutReturn();
      return;
    }
  }

  if (route) {
    const embedHome = getEmbedNavigationHomeUrl(resolveEmbedOrganizationContext(route));
    if (embedHome && (isEmbeddedContext() || route.query.embedOrganizationId)) {
      clearCheckoutReturn();
      router.push(embedHome);
      return;
    }
  }

  const hasSameOriginReferrer = typeof document !== 'undefined'
    && document.referrer
    && document.referrer.startsWith(window.location.origin)
    && !document.referrer.includes('/web/checkout');

  if (hasSameOriginReferrer) {
    clearCheckoutReturn();
    router.back();
    return;
  }

  clearCheckoutReturn();
  router.push(fallback);
}

export function buildCheckoutRouteQuery(
  route: RouteLocationNormalizedLoaded,
  extra: Record<string, string> = {},
): Record<string, string> {
  const query: Record<string, string> = { ...extra };

  Object.entries(route.query).forEach(([key, value]) => {
    if (value && !query[key]) {
      query[key] = String(value);
    }
  });

  if (route.path.includes('/embed/') && !query.embedOrganizationId) {
    const orgPathMatch = route.path.match(/\/embed\/organization\/([^/?]+)/);
    if (orgPathMatch) {
      query.embedOrganizationId = orgPathMatch[1];
    }
  }

  return query;
}

export function navigateToCheckout(
  router: Router,
  route: RouteLocationNormalizedLoaded,
  options?: {
    returnTo?: string;
    returnLabel?: string;
    query?: Record<string, string>;
  },
): void {
  const returnTo = options?.returnTo
    ?? (isCheckoutPath(route.path) ? undefined : route.fullPath);

  if (returnTo && isSafeCheckoutReturnPath(returnTo)) {
    saveCheckoutReturn({
      path: returnTo,
      label: options?.returnLabel,
    });
  }

  const query = buildCheckoutRouteQuery(route, {
    ...options?.query,
    ...(returnTo && isSafeCheckoutReturnPath(returnTo) ? { returnTo } : {}),
    ...(options?.returnLabel ? { returnLabel: options.returnLabel } : {}),
  });

  router.push({ path: '/web/checkout', query });
}
